// src/pages/AboutUs.jsx
import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  SimpleGrid,
  VStack,
  HStack,
  Avatar,
  Badge,
  Flex,
  useColorModeValue
} from '@chakra-ui/react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TeamMember = ({ name, role, image, description }) => {
  return (
    <VStack
      bg={useColorModeValue('white', 'gray.800')}
      p={6}
      borderRadius="lg"
      boxShadow="md"
      spacing={4}
      align="center"
      transition="all 0.3s"
      _hover={{
        transform: 'translateY(-5px)',
        boxShadow: 'lg',
      }}
    >
      <Avatar
        size="xl"
        src={image}
        name={name}
        border="3px solid"
        borderColor="brand.primary"
      />
      <VStack spacing={1} textAlign="center">
        <Heading as="h3" size="md" color="brand.primary">
          {name}
        </Heading>
        <Badge colorScheme="purple" px={2} py={1} borderRadius="full">
          {role}
        </Badge>
      </VStack>
      <Text color={useColorModeValue('gray.600', 'gray.400')} textAlign="center">
        {description}
      </Text>
    </VStack>
  );
};

const AboutUs = () => {
  return (
    <>
      <Header />
      <Box pt="100px" pb="60px" minH="100vh">
        <Container maxW="1200px">
          {/* Seção de Introdução */}
          <VStack spacing={8} mb={16} textAlign="center">
            <Heading 
              as="h1" 
              fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
              bgGradient="linear(to-r, brand.primary, brand.secondary)"
              bgClip="text"
            >
              Sobre o Draw Your Goods
            </Heading>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="800px">
              Somos uma plataforma criada para transformar suas memórias mais preciosas em arte para colorir.
              Nascemos da paixão por conectar pessoas com suas histórias de uma forma única e criativa.
            </Text>
          </VStack>

          {/* Nossa História */}
          <Box 
            bg="white" 
            borderRadius="xl" 
            p={{ base: 6, md: 10 }}
            boxShadow="md" 
            mb={16}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
              <Box>
                <Heading as="h2" size="xl" color="brand.primary" mb={4}>
                  Nossa História
                </Heading>
                <Text color="gray.600" fontSize="md" mb={3}>
                  A Draw Your Goods nasceu em 2024 quando nossa fundadora, apaixonada por arte e psicologia, 
                  descobriu o poder terapêutico de colorir como forma de reconexão com memórias pessoais.
                </Text>
                <Text color="gray.600" fontSize="md" mb={3}>
                  Após meses de pesquisa e desenvolvimento, criamos uma tecnologia que transforma 
                  fotografias em desenhos para colorir, preservando a essência dos momentos capturados 
                  enquanto proporciona uma experiência relaxante e significativa.
                </Text>
                <Text color="gray.600" fontSize="md">
                  Hoje, ajudamos pessoas de todo o Brasil a reconectar com suas memórias, 
                  oferecendo uma alternativa inovadora para lidar com o estresse do mundo digital 
                  e valorizar os momentos especiais de cada história.
                </Text>
              </Box>
              <Image 
                src="/img/aboutus.png" 
                alt="Nossa História" 
                borderRadius="lg"
                boxShadow="lg"
              />
            </SimpleGrid>
          </Box>

          {/* Nossa Missão e Valores */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} mb={16}>
            <Box
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="md"
              transition="all 0.3s"
              _hover={{
                transform: 'translateY(-5px)',
                boxShadow: 'lg',
              }}
            >
              <VStack align="start" spacing={4}>
                <Heading as="h3" size="md" color="brand.primary">
                  Nossa Missão
                </Heading>
                <Text color="gray.600">
                  Transformar memórias em experiências significativas através da arte, 
                  promovendo bem-estar e conexão com momentos especiais.
                </Text>
              </VStack>
            </Box>
            
            <Box
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="md"
              transition="all 0.3s"
              _hover={{
                transform: 'translateY(-5px)',
                boxShadow: 'lg',
              }}
            >
              <VStack align="start" spacing={4}>
                <Heading as="h3" size="md" color="brand.primary">
                  Nossa Visão
                </Heading>
                <Text color="gray.600">
                  Ser reconhecidos como a principal plataforma de transformação de memórias em arte, 
                  conectando pessoas às suas histórias de forma inovadora e terapêutica.
                </Text>
              </VStack>
            </Box>
            
            <Box
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="md"
              transition="all 0.3s"
              _hover={{
                transform: 'translateY(-5px)',
                boxShadow: 'lg',
              }}
            >
              <VStack align="start" spacing={4}>
                <Heading as="h3" size="md" color="brand.primary">
                  Nossos Valores
                </Heading>
                <Text color="gray.600">
                  Valorizamos a criatividade, inovação, bem-estar, conexão emocional e 
                  atenção aos detalhes em tudo o que fazemos.
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>

          {/* Nossa Equipe */}
          <VStack spacing={8} mb={16} textAlign="center">
            <Heading as="h2" size="xl" color="brand.primary" mb={4}>
              Nossa Equipe
            </Heading>
            <Text fontSize="md" color="gray.600" maxW="800px" mb={4}>
              Conheça as pessoas apaixonadas que tornam a Draw Your Goods possível.
            </Text>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="100%">
              <TeamMember
                name="Isis Aurora"
                role="Fundadora & CEO"
                image="/img/team-1.png"
                description="Apaixonada por arte e tecnologia, Ana fundou a Draw Your Goods com a missão de transformar memórias em experiências significativas."
              />
              <TeamMember
                name="Daniel Mello"
                role="Diretor de Tecnologia"
                image="/img/team-2.png"
                description="Especialista em IA e visão computacional, Lucas lidera o desenvolvimento de nossa tecnologia de transformação de imagens."
              />
              <TeamMember
                name="Juliana Mendes"
                role="Designer Chefe"
                image="/img/team-3.png"
                description="Com mais de 10 anos de experiência em design, Juliana garante que cada desenho preserve a essência dos momentos especiais."
              />
            </SimpleGrid>
          </VStack>

          {/* SEO - Schema.org markup for Google */}
          <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Draw Your Goods",
              "url": "https://www.drawyourgoods.com.br",
              "logo": "https://www.drawyourgoods.com.br/logo.png",
              "description": "Transforme suas memórias em arte para colorir e reconecte-se com momentos especiais de uma forma única e relaxante.",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "São Paulo",
                "addressRegion": "SP",
                "addressCountry": "BR"
              },
              "email": "drawgoodscontato@gmail.com",
              "telephone": "+5582991050175",
              "founder": {
                "@type": "Person",
                "name": "Ana Clara Silva"
              },
              "foundingDate": "2024"
            }
          `}</script>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default AboutUs;